import React, { useEffect, useState } from "react";
import { ArrowUpRight, Quote } from "lucide-react";

function initials(name) {
    return String(name || "")
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("") || "RF";
}

function cleanTestimonials(payload) {
    const rows = Array.isArray(payload?.testimonials) ? payload.testimonials : [];
    return rows
        .filter((item) => item?.quote && item?.name)
        .slice(0, 5)
        .map((item, index) => ({
            id: item.id || `testimonial-${index + 1}`,
            quote: String(item.quote || "").trim(),
            name: String(item.name || "").trim(),
            role: String(item.role || "").trim(),
            tag: String(item.tag || "Customer").trim() || "Customer",
            accent: Boolean(item.accent),
        }));
}

export default function Testimonials() {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch("/api/testimonials", { headers: { accept: "application/json" } })
            .then((response) => (response.ok ? response.json() : { testimonials: [] }))
            .then((payload) => {
                if (!cancelled) setItems(cleanTestimonials(payload));
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="border-b border-border bg-bg"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <span className="overline">What operators say</span>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-ink max-w-2xl">
                            Built by operators. Backed by real customer feedback.
                        </h2>
                    </div>
                    <p className="text-ink-soft max-w-md">
                        A small selection of reviewed ReachForge Leads customer
                        comments. Public reviews are managed from the admin panel.
                        <span className="block mt-4">
                            <a
                                href="/reviews"
                                data-testid="testimonials-review-link"
                                className="inline-flex items-center gap-2 text-ink font-semibold link-underline"
                            >
                                Leave a review <ArrowUpRight size={14} />
                            </a>
                        </span>
                    </p>
                </div>

                {items.length > 0 ? (
                    <div
                        className="grid grid-skeleton border border-border md:grid-cols-2 lg:grid-cols-3"
                        data-testid="testimonials-grid"
                    >
                        {items.map((t, i) => (
                            <article
                                key={t.id}
                                data-testid={`testimonial-${t.id}`}
                                className={`p-8 md:p-10 flex flex-col ${
                                    t.accent
                                        ? "bg-ink text-bg lg:row-span-2"
                                        : "bg-bg text-ink"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <Quote
                                        size={22}
                                        strokeWidth={2.25}
                                        className="text-forge"
                                    />
                                    <span
                                        className={`font-mono text-[11px] tracking-[0.2em] uppercase ${
                                            t.accent ? "text-bg/60" : "text-ink-mute"
                                        }`}
                                    >
                                        {String(i + 1).padStart(2, "0")} / {t.tag}
                                    </span>
                                </div>
                                <p
                                    className={`font-display text-lg md:text-xl leading-snug font-medium mt-6 ${
                                        t.accent ? "text-bg" : "text-ink"
                                    }`}
                                >
                                    “{t.quote}”
                                </p>
                                <div
                                    className={`mt-8 pt-5 border-t ${
                                        t.accent ? "border-bg/20" : "border-border"
                                    } flex items-center gap-3`}
                                >
                                    <div
                                        className={`h-9 w-9 flex items-center justify-center font-display font-bold text-sm ${
                                            t.accent
                                                ? "bg-bg text-ink"
                                                : "bg-ink text-bg"
                                        }`}
                                    >
                                        {initials(t.name)}
                                    </div>
                                    <div>
                                        <div
                                            className={`text-sm font-semibold ${
                                                t.accent ? "text-bg" : "text-ink"
                                            }`}
                                        >
                                            {t.name}
                                        </div>
                                        <div
                                            className={`text-xs ${
                                                t.accent
                                                    ? "text-bg/60"
                                                    : "text-ink-mute"
                                            }`}
                                        >
                                            {t.role || t.tag}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className="border border-border bg-surface p-8 md:p-10" data-testid="testimonials-empty">
                        <div className="overline">{loaded ? "No public reviews yet" : "Loading reviews"}</div>
                        <p className="text-ink-soft mt-3 max-w-2xl">
                            {loaded
                                ? "Customer reviews will appear here after they are added and reviewed in the admin panel."
                                : "Loading reviewed customer feedback from ReachForge."}
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
